import { useCallback, useMemo } from 'react';
import { css } from '@emotion/react';
import { Appointment, Status_Enum } from '@weave/schema-gen-ts/dist/schemas/appointments/v3/model.pb';
import {
  Direction_Enum,
  Field_Enum,
  Operator_Enum,
} from '@weave/schema-gen-ts/dist/schemas/appointments/v3/service.pb';
import dayjs from 'dayjs';
import minMax from 'dayjs/plugin/minMax';
import utc from 'dayjs/plugin/utc';
import { AppointmentsApiQueries } from '@frontend/api-appointments';
import { ScheduleQueries } from '@frontend/api-schedule';
import { StatusChip } from '@frontend/appointment-status';
import { useTranslation } from '@frontend/i18n';
import { StateRefHelpers } from '@frontend/state-ref-helpers';
import { theme } from '@frontend/theme';
import { Heading, SpinningLoader } from '@frontend/design-system';
import { ContactDetailsItem } from '../../../contact-details/contact-details-item';
import { CustomerDetailsProps } from '../../customer-details';
import { AppointmentDetails } from './appointment-details-card';

dayjs.extend(utc);
dayjs.extend(minMax);

interface PreparedAppointments {
  due: DueAppointment;
  last: PreparedAppointment;
  next: PreparedAppointment;
}

interface DueAppointment {
  data: string | null;
  label: string | Date;
}
interface PreparedAppointment {
  data: Appointment | null;
  label: string | Date;
}

interface AppointmentInformationProps {
  hasRecallIntegration: boolean;
  locationId: string;
}

export const AppointmentInformationV2 = ({
  data,
  locationId,
  hasRecallIntegration,
}: CustomerDetailsProps & AppointmentInformationProps) => {
  const { t } = useTranslation('schedule');

  const person = data;

  const { timestamp } = StateRefHelpers.useStableRoundedTimestamp({ roundingDirection: 'up', minutesToRound: 5 });

  const personFilter = {
    field: Field_Enum.PERSON_ID,
    operator: Operator_Enum.EQUAL,
    value: person?.PersonID ?? '',
  };

  const lastAppointmentFilters = {
    field: Field_Enum.APPOINTMENT_AT,
    operator: Operator_Enum.LT,
    value: timestamp.toISOString(),
  };

  const nextAppointmentFilters = {
    field: Field_Enum.APPOINTMENT_AT,
    operator: Operator_Enum.GT,
    value: timestamp.toISOString(),
  };

  const { data: lastAppointmentQuery } = AppointmentsApiQueries.useListAppointments(
    {
      filters: [personFilter, lastAppointmentFilters],
      sorts: [
        {
          field: Field_Enum.APPOINTMENT_AT,
          direction: Direction_Enum.DESC,
        },
      ],
      locationId: locationId,
      limit: 1,
    },
    {
      enabled: !!person?.PersonID && !!timestamp && !!locationId,
    }
  );

  const { data: nextAppointmentQuery } = AppointmentsApiQueries.useListAppointments(
    {
      filters: [personFilter, nextAppointmentFilters],
      sorts: [
        {
          field: Field_Enum.APPOINTMENT_AT,
          direction: Direction_Enum.ASC,
        },
      ],
      locationId: locationId,
      limit: 1,
    },
    {
      enabled: !!person?.PersonID && !!timestamp && !!locationId,
    }
  );

  const lastPersonAppointments = useMemo(
    () => lastAppointmentQuery?.appointments ?? [],
    [lastAppointmentQuery?.appointments]
  );
  const nextPersonAppointments = useMemo(
    () => nextAppointmentQuery?.appointments ?? [],
    [nextAppointmentQuery?.appointments]
  );

  const prepareAppointments = useCallback((): PreparedAppointments => {
    const lastAppointmentExists = !!person?.PersonID && !!lastPersonAppointments.length;
    const lastAppointment = lastAppointmentExists ? lastPersonAppointments[0] : null;
    const nextAppointmentExists = !!person?.PersonID && !!nextPersonAppointments.length;
    const nextAppointment = nextAppointmentExists ? getNextActiveAppointment(nextPersonAppointments) : null;
    const recalls = person?.Recall ?? [];
    const dueDate = recalls.map((recall) => {
      return dayjs.utc(recall.EventTime);
    });

    const dueDateActive = recalls.find((recall) => recall.EventStatus === 'Active');

    let dueAppointment;

    if (dueDateActive) {
      dueAppointment = dayjs.utc(dueDateActive.EventTime).format('MMMM D, YYYY');
    } else if (recalls.length) {
      dueAppointment = dayjs.min(dueDate).format('MMMM D, YYYY');
    } else {
      dueAppointment = null;
    }
    return {
      due: {
        data: dueAppointment,
        label: dueAppointment ? dueAppointment : 'None',
      },
      last: {
        data: lastAppointment,
        label: lastAppointment ? lastAppointment.appointmentAt : 'None',
      },
      next: {
        data: nextAppointment,
        label: nextAppointment ? nextAppointment.appointmentAt : 'None',
      },
    };
  }, [person, lastPersonAppointments, nextPersonAppointments]);

  const { last, next, due } = prepareAppointments();

  // Fetch the correct status for the next appointment
  const { data: appointmentData, isLoading: isAppointmentDataLoading } = ScheduleQueries.useGetAppointment({
    appointmentId: next.data?.appointmentId ?? '',
    locationId,
    isEnabled: !!next.data?.appointmentId && !!locationId,
  });

  const nextAppointmentStatus = appointmentData?.appointment?.statusOfficeView ?? '';

  if (!person) return null;

  return (
    <section css={{ padding: theme.spacing(2) }}>
      <div css={headingStyles}>
        <Heading level={3}>{t('Appointment Status')}</Heading>
        {isAppointmentDataLoading && <SpinningLoader size='small' />}
        {nextAppointmentStatus && <StatusChip status={nextAppointmentStatus.toUpperCase()} />}
      </div>
      {next?.data ? (
        <AppointmentDetails appointment={next?.data && next} locationId={locationId} />
      ) : (
        <>
          <ContactDetailsItem title={'Next Appointment'} value={'-'} />
        </>
      )}
      <section css={detailsItemStyles}>
        <ContactDetailsItem
          title={'Due'}
          value={due.data ? dayjs(due.label).format('MMMM D, YYYY') : '-'}
          omit={!hasRecallIntegration}
          css={css({
            p: { textWrap: 'wrap' },
          })}
        />
        <ContactDetailsItem
          title={'Last Appointment'}
          value={last.data ? dayjs(last.label).format('MMMM D, YYYY') : '-'}
          css={css({
            p: { textWrap: 'wrap' },
            span: { marginRight: theme.spacing(1) },
          })}
        />
      </section>
    </section>
  );
};

const headingStyles = css({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  marginBottom: theme.spacing(2),
});

const detailsItemStyles = css({
  display: 'flex',
  justifyContent: 'space-between',
  marginTop: theme.spacing(3),
  gap: theme.spacing(2),
});

const getNextActiveAppointment = (appointments: Appointment[]): Appointment | null => {
  const nextActiveAppointment = appointments.find(
    (appointment) => ![Status_Enum.CANCELED, Status_Enum.NO_SHOW].includes(appointment.status)
  );
  return nextActiveAppointment ?? appointments[0] ?? null;
};
